import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, MessageCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { adminAuthHeader } from "@/hooks/useAdminAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Kill-switch ON/OFF per il drip WhatsApp follow-up. Salvato in MongoDB
 * (`app_settings`), sovrascrive `AUTOMATED_MESSAGES_ENABLED` del `.env`.
 */
export default function WhatsAppDripToggle() {
  const [state, setState] = useState({ loading: true });
  const [toggling, setToggling] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/settings/whatsapp-drip`, { headers: adminAuthHeader() });
      setState({ loading: false, ...data });
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore nel caricamento dello stato.");
      setState({ loading: false });
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async () => {
    setToggling(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/admin/settings/whatsapp-drip`,
        { enabled: !state.enabled },
        { headers: adminAuthHeader() },
      );
      setState((s) => ({ ...s, enabled: data.enabled, source: "db", updated_at: data.updated_at }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Errore nel salvataggio.");
    } finally {
      setToggling(false);
    }
  };

  if (state.loading) {
    return (
      <section className="bg-white rounded-2xl border border-[#dbe3ec] shadow-[0_2px_30px_-15px_rgba(26,42,58,0.18)] mb-7 p-5">
        <Loader2 className="animate-spin" size={18} style={{ color: "var(--np-muted)" }} />
      </section>
    );
  }

  const onColor = state.enabled ? "#10b981" : "#ef4444";

  return (
    <section data-testid="whatsapp-drip-toggle" className="bg-white rounded-2xl border border-[#dbe3ec] shadow-[0_2px_30px_-15px_rgba(26,42,58,0.18)] mb-7 overflow-hidden">
      <div className="px-5 py-4 flex items-start gap-4 flex-wrap sm:flex-nowrap">
        <div className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: state.enabled ? "#E0F5E9" : "#FEE6E6", color: onColor }}>
          <MessageCircle size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-[15px] font-semibold" style={{ color: "var(--np-primary)" }}>WhatsApp follow-up automatico</h3>
            <span className="inline-flex items-center text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full"
              style={{ background: state.enabled ? "#E0F5E9" : "#FEE6E6", color: onColor }}>
              {state.enabled ? "ATTIVO" : "DISATTIVATO"}
            </span>
          </div>
          <p className="text-[13px] leading-relaxed" style={{ color: "var(--np-muted)" }}>
            {state.enabled
              ? "I lead che spuntano il consenso marketing ricevono il messaggio WhatsApp 3-5 min dopo la registrazione."
              : "Nessun messaggio WhatsApp viene inviato. I job vengono comunque schedulati ma marcati come 'skipped:disabled'."}
            {state.updated_at && <span className="ml-1.5 italic">— ultimo cambio: {new Date(state.updated_at).toLocaleString("it-IT")}</span>}
          </p>

          {!state.messenger_configured && (
            <p className="mt-2 text-[12px] flex items-center gap-1.5 text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
              <AlertTriangle size={12} /> Manca <code>MESSENGER_360_API_KEY</code> nel <code>.env</code> — anche se attivo, nessun messaggio partirà.
            </p>
          )}

          {error && (
            <p className="mt-2 text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5">{error}</p>
          )}
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={toggling}
          data-testid="whatsapp-drip-toggle-btn"
          className="shrink-0 inline-flex items-center gap-1.5 text-[13px] font-semibold px-4 py-2 rounded-full border transition disabled:opacity-60"
          style={{
            background: state.enabled ? "#ef4444" : "#10b981",
            color: "white",
            borderColor: state.enabled ? "#ef4444" : "#10b981",
          }}
        >
          {toggling
            ? <><Loader2 className="animate-spin" size={14} /> Salvo…</>
            : state.enabled
              ? "Disattiva"
              : <><CheckCircle2 size={14} /> Attiva ora</>}
        </button>
      </div>
    </section>
  );
}
